// src/pages/CalendarAdmin.jsx
import React, { useEffect, useRef, useState } from "react";
import api from "../api";
import { sortEndedLast, withEndedLabel } from "../utils/contractEnded";

const HISTORY_DEFAULT_MONTHS = 3;
// 탭 복귀 시 다시 읽기 — 마지막 조회 후 이만큼 지났을 때만(잦은 탭 전환에 매번 읽지 않게)
export const REFRESH_MIN_MS = 30000;
const HISTORY_PERIODS = [
  { months: 3, label: "최근 3개월" },
  { months: 6, label: "최근 6개월" },
  { months: 12, label: "최근 1년" },
  { months: 0, label: "전체" },
];

// 휴가시간 관리 표 "남은 시간 합계"(2026-10-22) = 연차 잔여 + 대체휴무 잔여. 특별휴가는 넣지 않는다.
// 원본 숫자로 더한 뒤 한 번만 소수 둘째 자리로 표기한다(각각 표기한 문자열을 더하지 않는다).
export const leaveTotalHours = (b) => Number(b.annual_leave_hours ?? 0) + Number(b.alt_leave_hours ?? 0);

// 특별휴가 잔여가 있는가 — 표에서 특별휴가 칸을 숨겼으므로 0이 아니면 합계 칸에 따로 드러낸다
export const hasSpecialLeave = (b) => Math.abs(Number(b.special_leave_hours ?? 0)) > 1e-9;

// 휴가 변동 이력 검색 — 종류·방향(2026-10-22). 종류는 서버 LEAVE_EVENT_TYPES 그대로, 방향은 시간 부호.
// 값 ""= 전체. 이름·종류·방향·기간은 전부 서버 쿼리로 AND 적용한다(화면에서 받아 거르지 않는다).
export const HISTORY_LEAVE_TYPES = ["연차", "반차", "대체휴무", "특별휴가"];
export const HISTORY_DIRECTIONS = [
  { value: "plus", label: "증가(+)" },
  { value: "minus", label: "차감(−)" },
];

// 오늘(브라우저 날짜)에서 N개월 전 — "YYYY-MM-DD"
function sinceDate(months) {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// 이력 검색 드롭다운(종류·방향) — 기간 드롭다운과 같은 모양
const HISTORY_SELECT_STYLE = {
  width: "120px", height: "36px", fontSize: "14px", border: "1px solid #bbb", borderRadius: "6px",
  backgroundColor: "white", color: "#333", boxSizing: "border-box", marginTop: "-11px",
};

// 저장 결과 안내 — 화면 위쪽 가운데에 한 줄(행 편집·모바일 모달 어디서 저장해도 보이게)
const SAVE_STATUS_STYLE = {
  checking: { background: "#fff8e1", color: "#8a6d00", border: "1px solid #f0d27a" },
  saved: { background: "#e8f5e9", color: "#1b5e20", border: "1px solid #a5d6a7" },
  failed: { background: "#fdecea", color: "#b71c1c", border: "1px solid #f5a3a0" },
};

export default function CalendarAdmin() {
  const [balances, setBalances] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    annual_leave_hours_delta: "",
    alt_leave_hours_delta: "",
    special_leave_hours_delta: "",
    last_work_date: "",
    reason: ""
  });
  const [originalValues, setOriginalValues] = useState(null);
  const [loading, setLoading] = useState(false);
  // 저장 결과 안내 — null | {kind: "checking" | "saved" | "failed", text}
  // 저장 요청이 실패·시간 초과돼도 서버에는 저장됐을 수 있다(서버가 잠깐 멈춘 경우 실측).
  // 그래서 바로 "실패"라 하지 않고 다시 읽어 확인한다. 확인이 끝날 때까지 저장 버튼은 잠근다.
  const [saveStatus, setSaveStatus] = useState(null);

  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [history, setHistory] = useState([]);
  // 이력 조회 기간(개월) — 기본 최근 3개월, 0 = 전체(2026-10-15). 전체 이력(수천 건)을 매번
  // 받아 그리던 것을 줄이려는 것이다.
  const [historyMonths, setHistoryMonths] = useState(HISTORY_DEFAULT_MONTHS);

  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");
  const [historyLeaveType, setHistoryLeaveType] = useState("");   // "" = 전체
  const [historyDirection, setHistoryDirection] = useState("");   // "" = 전체

  // ✅ 모바일 감지 + 모달 상태
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [modalTarget, setModalTarget] = useState(null);

  useEffect(() => {



    const handleResize = () => setIsMobile(window.innerWidth < 768);


    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  document.title = "캘린더(관리자)";

  const fetchUser = async () => {
    try {
      const res = await api.get("/auth/me/");
      setUser(res.data);
    } catch (err) {
      console.error("❌ 사용자 정보 불러오기 실패:", err);
    } finally {
      setAuthChecked(true);
    }
  };

  // 마지막 조회 시각(잔여·이력) — 탭 복귀 갱신의 30초 판정 기준
  const lastLoadedRef = useRef(0);

  const fetchBalances = async () => {
    lastLoadedRef.current = Date.now();
    try {
      const res = await api.get("/calendar/leave-balance/");
      setBalances(res.data);
    } catch (err) {
      console.error("❌ 휴가시간 데이터 불러오기 실패:", err);
    }
  };

  // 지금 화면의 검색 조건(기간·이름·종류·방향) — 바꾼 값은 overrides로 넘긴다(state 반영 전이라)
  const historyFilters = (overrides = {}) => ({
    months: historyMonths, name: activeSearchQuery, leaveType: historyLeaveType,
    direction: historyDirection, ...overrides,
  });

  const historyParams = (filters, userId) => {
    const params = {};
    if (filters.months > 0) params.since = sinceDate(filters.months);
    if (filters.name) params.name = filters.name;
    if (filters.leaveType) params.leave_type = filters.leaveType;
    if (filters.direction) params.direction = filters.direction;
    if (userId) params.user = userId;
    return params;
  };

  // 조건을 빠르게 바꾸면 응답 순서가 뒤집힐 수 있다 — 마지막 요청의 응답만 쓴다
  const historyRequestRef = useRef(0);

  const fetchHistory = async (overrides = {}) => {
    lastLoadedRef.current = Date.now();
    const seq = ++historyRequestRef.current;
    try {
      const res = await api.get("/calendar/leave-history/", { params: historyParams(historyFilters(overrides)) });
      if (seq === historyRequestRef.current) setHistory(res.data);
    } catch (err) {
      console.error("❌ 휴가 이력 불러오기 실패:", err);
    }
  };

  // 저장 뒤 — 바뀐 직원 이력만 다시 받아 그 직원 줄만 바꾼다(전체를 다시 받지 않는다).
  const fetchHistoryFor = async (userId) => {
    try {
      const res = await api.get("/calendar/leave-history/", {
        params: historyParams(historyFilters(), userId),
      });
      setHistory((prev) =>
        [...prev.filter((h) => h.user !== userId), ...res.data].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )
      );
    } catch (err) {
      console.error("❌ 휴가 이력 불러오기 실패:", err);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (user) {
      const rank = parseInt(user.position_rank, 10);
      if (rank === 2 || rank === 3) {
        fetchBalances();
        fetchHistory();
      }
    }
  }, [user]);



  // ✅ handleEdit: 모바일이면 모달, PC면 기존 인라인
  const handleEdit = (balance) => {
    const vals = {
      annual_leave_hours: balance.annual_leave_hours,
      alt_leave_hours: balance.alt_leave_hours,
      special_leave_hours: balance.special_leave_hours,
    };
    setOriginalValues(vals);
    setForm({
      annual_leave_hours_delta: "",
      alt_leave_hours_delta: "",
      special_leave_hours_delta: "",
      last_work_date: balance.last_work_date || "",
      reason: ""
    });

    if (isMobile) {
      setModalTarget(balance);
    } else {
      setEditing(balance.id);
    }
  };

  // 저장이 실제로 반영됐는가 — 잔여시간을 다시 읽어 보낸 값과 비교한다.
  // 서버가 바쁠 수 있어 넉넉한 시간 제한으로 최대 3번 읽는다. 끝내 못 읽으면 "실패"로 보고
  // 다시 시도하게 한다 — 보내는 값이 절대값이라 이미 저장됐어도 다시 저장하면 같은 값이 되고
  // (이력도 바뀐 항목만 남으므로) 두 번 더해지지 않는다.
  const confirmSaved = async (targetId, expected) => {
    const same = (a, b) => Math.abs(Number(a) - Number(b)) < 1e-6;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const res = await api.get("/calendar/leave-balance/", { timeout: 30000 });
        setBalances(res.data);
        const row = res.data.find((b) => b.id === targetId);
        return Boolean(
          row &&
            same(row.annual_leave_hours, expected.annual_leave_hours) &&
            same(row.alt_leave_hours, expected.alt_leave_hours) &&
            same(row.special_leave_hours, expected.special_leave_hours) &&
            (row.last_work_date || null) === expected.last_work_date
        );
      } catch (err) {
        console.error("❌ 저장 확인(다시 읽기) 실패:", err);
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }
    return false;
  };

  const saving = loading || saveStatus?.kind === "checking";

  // ✅ handleSave: overrideId 파라미터 추가 (모달에서 호출 시 사용) → 저장됐으면 true
  const handleSave = async (overrideId = null) => {
    const targetId = overrideId ?? editing;
    if (saving) return false;
    setLoading(true);
    setSaveStatus(null);

    const annualDelta = parseFloat(form.annual_leave_hours_delta) || 0;
    const altDelta = parseFloat(form.alt_leave_hours_delta) || 0;
    const specialDelta = parseFloat(form.special_leave_hours_delta) || 0;

    const expected = {
      annual_leave_hours: originalValues.annual_leave_hours + annualDelta,
      alt_leave_hours: originalValues.alt_leave_hours + altDelta,
      special_leave_hours: originalValues.special_leave_hours + specialDelta,
      // 마지막 근로일 — 빈 값이면 null(삭제 = 재직). 휴가 이력에는 남지 않는다.
      last_work_date: form.last_work_date || null,
    };

    let saved = false;
    try {
      await api.patch(`/calendar/leave-balance/${targetId}/`, { ...expected, reason: form.reason });
      saved = true;
    } catch (err) {
      console.error("❌ 저장 요청 실패 — 저장됐는지 다시 읽어 확인:", err);
      setSaveStatus({ kind: "checking", text: "저장 확인 중…" });
      saved = await confirmSaved(targetId, expected);
    }

    if (saved) {
      const target = balances.find((b) => b.id === targetId);
      await fetchBalances();
      if (target) await fetchHistoryFor(target.user);
      else await fetchHistory();
      setEditing(null);
      setOriginalValues(null);
      setSaveStatus({ kind: "saved", text: "저장됨" });
      setTimeout(() => setSaveStatus((s) => (s?.kind === "saved" ? null : s)), 3000);
    } else {
      // 입력한 값은 그대로 둔다 — 저장 버튼을 다시 누르면 같은 값으로 다시 보낸다.
      setSaveStatus({ kind: "failed", text: "저장 실패 — 다시 시도" });
    }
    setLoading(false);
    return saved;
  };

  // 탭 복귀 시 잔여·이력 다시 읽기(2026-10-21) — 이 화면은 처음 열 때와 여기서 저장할 때만 읽어서,
  // 다른 탭에서 승인한 휴가신청서 이력이 새로고침 전까지 안 보였다(10/21 문서 620: 조회 09:50:41,
  // 승인 09:51:13). 마지막 조회 후 30초가 지났을 때만 읽고, 수정 중(행 편집·모바일 모달·저장 확인
  // 중)에는 보류한다 — 입력값을 지키려는 것이다. 보류된 갱신은 다음 탭 복귀 때 한다.
  const refreshIfStaleRef = useRef(null);
  refreshIfStaleRef.current = () => {
    if (document.visibilityState === "hidden") return;
    const r = parseInt(user?.position_rank, 10);
    if (r !== 2 && r !== 3) return;
    if (editing !== null || modalTarget || saving) return;
    if (Date.now() - lastLoadedRef.current < REFRESH_MIN_MS) return;
    fetchBalances();
    fetchHistory();
  };

  useEffect(() => {
    const onReturn = () => refreshIfStaleRef.current();
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, []);

  // 서버가 이미 조건대로 걸러 보낸다(이름·종류·방향·기간)
  const filteredHistory = history;
  const historyFiltered = Boolean(activeSearchQuery || historyLeaveType || historyDirection);

  const handleSearch = () => {
    const name = searchQuery.trim();
    setActiveSearchQuery(name);
    fetchHistory({ name });
  };

  const handleReset = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    setHistoryLeaveType("");
    setHistoryDirection("");
    fetchHistory({ name: "", leaveType: "", direction: "" });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // ✅ 마지막 근로일 입력 — 날짜 선택 + 지우기(×). 지우면 재직으로 돌아간다.
  // ⚠️ 지우기 버튼은 크기를 명시한다(전역 button{width:100%} 누수 방지 — CLAUDE.md).
  const renderLastWorkDateInput = () => (
    <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "center", whiteSpace: "nowrap" }}>
      <input
        type="date"
        value={form.last_work_date}
        onChange={(e) => setForm({ ...form, last_work_date: e.target.value })}
        style={{ width: "130px", padding: "3px", fontSize: "13px" }}
      />
      <button
        type="button"
        title="마지막 근로일 삭제(재직)"
        onClick={() => setForm({ ...form, last_work_date: "" })}
        disabled={!form.last_work_date}
        style={{
          width: "28px", height: "28px", minWidth: "28px", flex: "0 0 28px", padding: 0,
          border: "1px solid #ccc", borderRadius: "4px", backgroundColor: "white",
          color: "#666", cursor: form.last_work_date ? "pointer" : "default", lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  if (!authChecked) {
    return <div style={{ padding: 20 }}>⏳ 권한 확인 중...</div>;
  }

  const rank = parseInt(user?.position_rank, 10);
  if (!user || (rank !== 2 && rank !== 3)) {
    return <div style={{ padding: 20, color: "red" }}>🚫 권한이 없습니다</div>;
  }

  return (
    <div style={{ padding: 20 }}>
      <h1>📌 캘린더(관리자)</h1>

      {/* 저장 결과 — 저장 확인 중 / 저장됨 / 저장 실패 — 다시 시도 (모달보다 위에 뜬다) */}
      {saveStatus && (
        <div
          role="status"
          style={{
            position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 10000,
            padding: "8px 16px", borderRadius: 8, fontSize: 14, fontWeight: "bold",
            whiteSpace: "nowrap", boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            ...SAVE_STATUS_STYLE[saveStatus.kind],
          }}
        >
          {saveStatus.text}
        </div>
      )}

      {/* ✅ 모바일 수정 모달 */}
      {isMobile && modalTarget && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999,
          padding: "20px",
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "24px",
            width: "100%",
            maxWidth: "400px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          }}>
            <h3 style={{ margin: "0 0 16px 0" }}>
              ✏️ {modalTarget.user_name} {modalTarget.position} 휴가 수정
            </h3>

            {[
              { label: "연차", key: "annual_leave_hours_delta", orig: originalValues?.annual_leave_hours },
              { label: "대체휴무", key: "alt_leave_hours_delta", orig: originalValues?.alt_leave_hours },
              { label: "특별휴가", key: "special_leave_hours_delta", orig: originalValues?.special_leave_hours },
            ].map(({ label, key, orig }) => (
              <div key={key} style={{ marginBottom: "12px" }}>
                <label style={{ fontSize: "13px", color: "#555", display: "block", marginBottom: "4px" }}>
                  {label} (현재: <b>{(orig ?? 0).toFixed(2)}시간</b>)
                </label>
                <input
                  type="text"
                  placeholder="+5 또는 -3.25"
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  style={{
                    width: "100%", padding: "8px 10px", fontSize: "14px",
                    border: "1px solid #ccc", borderRadius: "6px", boxSizing: "border-box",
                  }}
                />
              </div>
            ))}

            <div style={{ marginBottom: "12px" }}>
              <label style={{ fontSize: "13px", color: "#555", display: "block", marginBottom: "4px", whiteSpace: "nowrap" }}>
                마지막 근로일 <span style={{ color: "#999" }}>(재직자는 비움)</span>
              </label>
              {renderLastWorkDateInput()}
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "13px", color: "#555", display: "block", marginBottom: "4px" }}>
                사유 <span style={{ color: "#999" }}>(작성자: {user?.name} {user?.position})</span>
              </label>
              <textarea
                placeholder="사유를 입력하세요"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                style={{
                  width: "100%", minHeight: "60px", padding: "8px 10px", fontSize: "14px",
                  border: "1px solid #ccc", borderRadius: "6px", boxSizing: "border-box", resize: "vertical",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                onClick={async () => {
                  // 저장이 확인됐을 때만 닫는다 — 실패면 입력값을 둔 채 다시 시도하게.
                  if (await handleSave(modalTarget.id)) setModalTarget(null);
                }}
                disabled={saving}
                style={{
                  flex: 1, padding: "10px", fontSize: "15px", fontWeight: "bold",
                  backgroundColor: "#1976d2", color: "white",
                  border: "none", borderRadius: "8px", cursor: "pointer",
                }}
              >
                {saving ? "저장중..." : "저장"}
              </button>
              <button
                onClick={() => {
                  setModalTarget(null);
                  setOriginalValues(null);
                }}
                style={{
                  flex: 1, padding: "10px", fontSize: "15px",
                  backgroundColor: "#f5f5f5", color: "#333",
                  border: "1px solid #ccc", borderRadius: "8px", cursor: "pointer",
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ 휴가 잔여시간 관리 */}
      <div style={{ marginBottom: "40px" }}>
        <h2>👥 휴가시간 관리</h2>

        {isMobile ? (
          /* ✅ 모바일: 카드형 */
          <div style={{ marginTop: "16px" }}>
            {/* 계약종료자 맨 아래 — 판정은 서버 is_contract_ended(이름 문자열 판정 폐기) */}
            {sortEndedLast(balances
              .filter(b => {
                const name = b.user_name || '';
                if (!name.trim()) return false;
                if (name.includes('테스트')) return false;
                return true;
              }))
              .map((b) => (
                <div key={b.id} style={{
                  border: "1px solid #ddd",
                  borderRadius: "10px",
                  padding: "14px 16px",
                  marginBottom: "12px",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                }}>
                  {/* 직원명 + 직급 헤더 */}
                  <div style={{
                    fontSize: "15px",
                    fontWeight: "bold",
                    marginBottom: "10px",
                    paddingBottom: "8px",
                    borderBottom: "2px solid #e0e0e0",
                    color: "#1976d2",
                  }}>
                    {b.user_name}{" "}
                    <span style={{ fontSize: "13px", color: "#666", fontWeight: "normal" }}>
                      {withEndedLabel(b.position || "", b.is_contract_ended)}
                    </span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", padding: "5px 0", borderBottom: "1px solid #f0f0f0" }}>
                    <span style={{ minWidth: "72px", fontSize: "12px", color: "#888", fontWeight: "bold", whiteSpace: "nowrap" }}>
                      마지막 근로일
                    </span>
                    <span style={{ fontSize: "13px", color: "#333", marginLeft: "8px" }}>
                      {b.last_work_date || "-"}
                    </span>
                  </div>

                  {/* 휴가 잔여시간 3종 */}
                  {[
                    { label: "연차", hours: b.annual_leave_hours },
                    { label: "대체휴무", hours: b.alt_leave_hours },
                    { label: "특별휴가", hours: b.special_leave_hours },
                  ].map(({ label, hours }) => (
                    <div key={label} style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "5px 0",
                      borderBottom: "1px solid #f0f0f0",
                    }}>
                      <span style={{
                        minWidth: "72px",
                        fontSize: "12px",
                        color: "#888",
                        fontWeight: "bold",
                      }}>
                        {label}
                      </span>
                      <span style={{ fontSize: "13px", color: "#333" }}>
                        {(hours ?? 0).toFixed(2)} 시간
                      </span>
                    </div>
                  ))}

                  {/* 수정 버튼 */}
                  <div style={{ marginTop: "10px" }}>
                    <button
                      type="button"
                      onClick={() => handleEdit(b)}
                      style={{
                        width: "100%",
                        padding: "8px",
                        fontSize: "14px",
                        cursor: "pointer",
                        border: "1px solid #1976d2",
                        borderRadius: "6px",
                        backgroundColor: "white",
                        color: "#1976d2",
                        fontWeight: "bold",
                      }}
                    >
                      ✏️ 수정
                    </button>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          /* ✅ PC: 기존 테이블 그대로 */
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "20px",
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f0f0f0" }}>
                {[
                  "직원",
                  "직급",
                  "마지막 근로일",
                  "연차(남은 시간)",
                  "대체휴무(남은 시간)",
                  // 특별휴가 칸은 숨겼다(2026-10-22) — 값·수정은 그대로, 잔여가 있으면 합계 칸에 배지
                  "남은 시간 합계",
                  "관리",
                ].map((header) => (



                  <th
                    key={header}
                    style={{
                      border: "1px solid #ccc",
                      padding: "8px",
                      textAlign: "center",
                      width:
                        // 직원 칸은 "이름 (계약종료)"가 한 줄에 들어가게 넓혔다(줄바꿈 금지 규칙)
                        header === "직원" ? "12%" :
                        header === "직급" ? "8%" :
                        header === "마지막 근로일" ? "14%" :
                        header === "관리" ? "20%" :
                        "14%",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* 계약종료자 맨 아래 — 판정은 서버 is_contract_ended(이름 문자열 판정 폐기) */}
              {sortEndedLast(balances
                .filter(b => {
                  const name = b.user_name || '';
                  if (!name.trim()) return false;
                  if (name.includes('테스트')) return false;
                  return true;
                }))
                .map((b) => (
                  <tr key={b.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center", whiteSpace: "nowrap" }}>
                      {withEndedLabel(b.user_name, b.is_contract_ended)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                      {b.position}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center", whiteSpace: "nowrap" }}>
                      {editing === b.id ? renderLastWorkDateInput() : (b.last_work_date || "-")}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                      {editing === b.id ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                          <span style={{
                            fontWeight: "bold", lineHeight: "24px", backgroundColor: "white",
                            border: "1px solid #ddd", padding: "4px 8px", borderRadius: "6px",
                            verticalAlign: "middle", display: "inline-block",
                          }}>
                            {(originalValues.annual_leave_hours ?? 0).toFixed(2)}
                          </span>
                          <input
                            type="text" placeholder="+5 또는 -3.25"
                            style={{ width: "100px", textAlign: "center", padding: "4px" }}
                            value={form.annual_leave_hours_delta}
                            onChange={(e) => setForm({ ...form, annual_leave_hours_delta: e.target.value })}
                          />
                        </div>
                      ) : (
                        (b.annual_leave_hours ?? 0).toFixed(2)
                      )}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                      {editing === b.id ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                          <span style={{
                            fontWeight: "bold", lineHeight: "24px", backgroundColor: "white",
                            border: "1px solid #ddd", padding: "4px 8px", borderRadius: "6px",
                            verticalAlign: "middle", display: "inline-block",
                          }}>
                            {(originalValues.alt_leave_hours ?? 0).toFixed(2)}
                          </span>
                          <input
                            type="text" placeholder="+5 또는 -3.25"
                            style={{ width: "100px", textAlign: "center", padding: "4px" }}
                            value={form.alt_leave_hours_delta}
                            onChange={(e) => setForm({ ...form, alt_leave_hours_delta: e.target.value })}
                          />
                        </div>
                      ) : (
                        (b.alt_leave_hours ?? 0).toFixed(2)
                      )}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center", whiteSpace: "nowrap" }}>
                      {editing === b.id ? (
                        // 수정 중 — 숨긴 특별휴가 칸의 입력을 여기서 그대로 받는다(모바일 수정 창과 같은 항목)
                        // 칸이 좁아(14%) 두 줄로 — 위: "특휴 현재값", 아래: 증감 입력(각 줄은 줄바꿈 없음)
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }}>
                            <span style={{ fontSize: "12px", color: "#888", fontWeight: "bold", whiteSpace: "nowrap" }}>
                              특휴
                            </span>
                            <span style={{
                              fontWeight: "bold", lineHeight: "24px", backgroundColor: "white",
                              border: "1px solid #ddd", padding: "4px 8px", borderRadius: "6px",
                              verticalAlign: "middle", display: "inline-block",
                            }}>
                              {(originalValues.special_leave_hours ?? 0).toFixed(2)}
                            </span>
                          </div>
                          <input
                            type="text" placeholder="+5 또는 -3.25" aria-label="특별휴가 증감"
                            style={{ width: "100px", textAlign: "center", padding: "4px" }}
                            value={form.special_leave_hours_delta}
                            onChange={(e) => setForm({ ...form, special_leave_hours_delta: e.target.value })}
                          />
                        </div>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }}>
                          <span data-testid="leave-total">{leaveTotalHours(b).toFixed(2)}</span>
                          {hasSpecialLeave(b) && (
                            <span
                              className="leave-special-badge"
                              title={`특별휴가 잔여 ${Number(b.special_leave_hours).toFixed(2)}시간 — 합계에는 포함하지 않음`}
                              style={{
                                fontSize: "11px", padding: "1px 6px", borderRadius: "10px", whiteSpace: "nowrap",
                                background: "#fff3e0", color: "#e65100", border: "1px solid #ffcc80",
                              }}
                            >
                              + 특휴 {Number(b.special_leave_hours).toFixed(2)}
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                      {editing === b.id ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                            작성자: <b>{user?.name} {user?.position}</b>
                          </div>
                          <textarea
                            placeholder="사유를 입력하세요"
                            style={{ width: "100%", minHeight: "40px" }}
                            value={form.reason}
                            onChange={(e) => setForm({ ...form, reason: e.target.value })}
                          />
                          <button type="button" onClick={() => handleSave()} disabled={saving}
                            style={{ padding: "5px 10px", fontSize: "13px", cursor: "pointer",
                              border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "white", color: "#333" }}>
                            {saving ? "저장중..." : "저장"}
                          </button>
                          <button type="button"
                            onClick={() => { setEditing(null); setOriginalValues(null); }}
                            style={{ padding: "5px 10px", fontSize: "13px", cursor: "pointer",
                              border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "white", color: "#333" }}>
                            취소
                          </button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => handleEdit(b)}
                          style={{ padding: "5px 10px", fontSize: "13px", cursor: "pointer",
                            border: "1px solid #ddd", borderRadius: "4px", backgroundColor: "white", color: "#333" }}>
                          수정
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}

      </div>


      {/* ✅ 휴가 변동 이력 테이블 */}
      <div>
        <h2>
          📜 휴가 변동 이력{" "}
          <span style={{ fontSize: "16px", fontWeight: "normal", color: "#555", whiteSpace: "nowrap" }}>
            ({HISTORY_PERIODS.find((p) => p.months === historyMonths)?.label})
          </span>
        </h2>
        {/* 기본이 최근 3개월이라 예전 이력이 "사라졌다"고 오해하지 않게 — 기간 안내를 눈에 띄게 둔다 */}
        {historyMonths > 0 && (
          <div
            style={{
              display: "inline-flex", alignItems: "center", gap: "8px", marginBottom: "12px",
              padding: "8px 12px", borderRadius: "6px", fontSize: "14px", whiteSpace: "nowrap",
              background: "#e3f2fd", color: "#0d47a1", border: "1px solid #90caf9",
            }}
          >
            📅 {HISTORY_PERIODS.find((p) => p.months === historyMonths)?.label} 이력만 보입니다.
            <button
              type="button"
              onClick={() => {
                setHistoryMonths(0);
                fetchHistory({ months: 0 });
              }}
              style={{
                width: "110px", height: "30px", fontSize: "13px", fontWeight: "bold", cursor: "pointer",
                border: "1px solid #1976d2", borderRadius: "6px", background: "white", color: "#1976d2",
                whiteSpace: "nowrap",
              }}
            >
              전체 이력 보기
            </button>
          </div>
        )}


        <div
          style={{
            marginBottom: "15px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "nowrap",
            whiteSpace: "nowrap",
            width: "fit-content",
          }}
        >
          <input
            type="text"
            placeholder="직원 이름으로 검색"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            style={{
              padding: "0px 10px",
              width: "200px",
              height: "36px",
              fontSize: "14px",
              border: "1px solid #bbb",
              borderRadius: "6px",
              backgroundColor: "white",
              color: "#333",
              boxSizing: "border-box",
              lineHeight: "36px",
              verticalAlign: "bottom",
            }}
          />

          {/* 종류·방향 — 바꾸면 바로 서버에서 다시 거른다(이름은 검색 버튼·Enter로) */}
          <select
            aria-label="종류"
            value={historyLeaveType}
            onChange={(e) => {
              setHistoryLeaveType(e.target.value);
              fetchHistory({ leaveType: e.target.value });
            }}
            style={HISTORY_SELECT_STYLE}
          >
            <option value="">종류: 전체</option>
            {HISTORY_LEAVE_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          <select
            aria-label="방향"
            value={historyDirection}
            onChange={(e) => {
              setHistoryDirection(e.target.value);
              fetchHistory({ direction: e.target.value });
            }}
            style={HISTORY_SELECT_STYLE}
          >
            <option value="">방향: 전체</option>
            {HISTORY_DIRECTIONS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>

          <button
            type="button"
            onClick={handleSearch}
            style={{
              padding: "0px 10px",
              width: "70px",
              height: "36px",
              fontSize: "14px",
              cursor: "pointer",
              border: "1px solid #bbb",
              borderRadius: "6px",
              backgroundColor: "white",
              color: "#333",
              boxSizing: "border-box",
              lineHeight: "36px",
              verticalAlign: "bottom",
              marginTop: "-11px",
            }}
          >
            검색
          </button>

          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: "0px 10px",
              width: "70px",
              height: "36px",
              fontSize: "14px",
              cursor: "pointer",
              border: "1px solid #bbb",
              borderRadius: "6px",
              backgroundColor: "white",
              color: "#333",
              boxSizing: "border-box",
              lineHeight: "36px",
              verticalAlign: "bottom",
              marginTop: "-11px",
            }}
          >
            초기화
          </button>

          {/* 조회 기간 — 기본 최근 3개월. 바꾸면 그 기간으로 다시 읽는다. */}
          <select
            aria-label="기간"
            value={historyMonths}
            onChange={(e) => {
              const months = Number(e.target.value);
              setHistoryMonths(months);
              fetchHistory({ months });
            }}
            style={{
              width: "110px",
              height: "36px",
              fontSize: "14px",
              border: "1px solid #bbb",
              borderRadius: "6px",
              backgroundColor: "white",
              color: "#333",
              boxSizing: "border-box",
              marginTop: "-11px",
            }}
          >
            {HISTORY_PERIODS.map((p) => (
              <option key={p.months} value={p.months}>
                {p.label}
              </option>
            ))}
          </select>

          <span style={{ fontSize: "14px", color: "#666", marginLeft: "8px" }}>
            {filteredHistory.length}개의 결과
          </span>
        </div>

        {/* ✅ 모바일: 카드형 세로 레이아웃 */}
        {isMobile ? (
          <div style={{ marginTop: "16px" }}>
            {filteredHistory.length === 0 ? (
              <div style={{
                padding: "20px",
                textAlign: "center",
                color: "#999",
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}>
                {historyFiltered
                  ? "검색 조건에 맞는 이력이 없습니다."
                  : "변동 이력이 없습니다."}
              </div>
            ) : (
              filteredHistory.map((h) => (
                <div key={h.id} style={{
                  border: "1px solid #ddd",
                  borderRadius: "10px",
                  padding: "14px 16px",
                  marginBottom: "12px",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                }}>
                  {[
                    {
                      label: "작성일",
                      value: new Date(h.created_at).toLocaleString('ko-KR', {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit',
                      }),
                    },
                    { label: "해당일", value: h.event_date ? formatDate(h.event_date) : '-' },
                    { label: "직원", value: h.user_name },
                    { label: "직급", value: h.position },
                    { label: "휴가종류", value: h.leave_type },
                    {
                      label: "변동시간",
                      value: h.hours > 0
                        ? `+${(h.hours ?? 0).toFixed(2)} 시간`
                        : `${(h.hours ?? 0).toFixed(2)} 시간`,
                      color: h.hours < 0 ? "red" : "green",
                    },
                    { label: "사유", value: h.reason },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{
                      display: "flex",
                      alignItems: "flex-start",
                      padding: "5px 0",
                      borderBottom: "1px solid #f0f0f0",
                    }}>
                      <span style={{
                        minWidth: "72px",
                        fontSize: "12px",
                        color: "#888",
                        fontWeight: "bold",
                        paddingTop: "1px",
                      }}>
                        {label}
                      </span>
                      <span style={{
                        fontSize: "13px",
                        color: color ?? "#333",
                        fontWeight: color ? "bold" : "normal",
                        flex: 1,
                      }}>
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        ) : (
          /* ✅ PC: 기존 테이블 그대로 */
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "20px",
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f0f0f0" }}>
                {["작성일", "해당일", "직원", "직급", "휴가종류", "변동시간", "사유"].map((header) => (
                  <th
                    key={header}
                    style={{
                      border: "1px solid #ccc",
                      padding: "8px",
                      textAlign: "center",
                      width:
                        header === "직원" ? "7%" :
                        header === "직급" ? "7%" :
                        header === "작성일" ? "12%" :
                        header === "해당일" ? "10%" :
                        header === "휴가종류" ? "10%" :
                        header === "변동시간" ? "10%" :
                        header === "사유" ? "30%" :
                        "auto",
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredHistory.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      border: "1px solid #ccc",
                      padding: "20px",
                      textAlign: "center",
                      color: "#999"
                    }}
                  >
                    {historyFiltered
                      ? "검색 조건에 맞는 이력이 없습니다."
                      : "변동 이력이 없습니다."}
                  </td>
                </tr>
              ) : (
                filteredHistory.map((h) => (
                  <tr key={h.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                      {new Date(h.created_at).toLocaleString('ko-KR', {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                      {h.event_date ? formatDate(h.event_date) : '-'}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                      {h.user_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                      {h.position}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                      {h.leave_type}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                        color: h.hours < 0 ? "red" : "green",
                      }}
                    >
                      {h.hours > 0 ? `+${(h.hours ?? 0).toFixed(2)}` : (h.hours ?? 0).toFixed(2)} 시간
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px", textAlign: "center" }}>
                      {h.reason}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}


      </div>
    </div>
  );
}

